import CheckIcon from '@mui/icons-material/Check';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { CHATYME_EMBED_HOST_MESSAGE_TYPE } from '../constants/chatymeEmbedHostMessageTypes';
import { EUROPEAN_LANGUAGES } from '../constants/languages';
import { StorageHelper } from '../helpers';
import { useApiClient } from '../hooks/useApiClient';
import { ChatMessageTemplateService } from '../services';
import { LiveChatData } from '../types/client';
import { postChatEmbedHostMessage } from '../utils/chatEmbedHost';
import NestedMenuItem from './common/NestedMenuItem';

interface ChatAgentConfigProps {
  selectedLanguage: string;
  onLanguageChange: (langCode: string) => void;
  chatData?: LiveChatData | null;
}

const syncAgentConfigToParent = (): void => {
  if (window.parent === window) {
    return;
  }

  const agentConfig = StorageHelper.getAgentConfigSync();
  let parentOrigin = '*';

  try {
    if (document.referrer) {
      parentOrigin = new URL(document.referrer).origin;
    }
  } catch {
    parentOrigin = '*';
  }

  postChatEmbedHostMessage(
    {
      type: CHATYME_EMBED_HOST_MESSAGE_TYPE.AGENT_CONFIG_UPDATED,
      config: {
        language: agentConfig.language,
        notification: agentConfig.notification
      }
    },
    parentOrigin
  );
};

const persistAgentConfig = (partial: Parameters<typeof StorageHelper.setAgentConfigSync>[0]): void => {
  StorageHelper.setAgentConfigSync(partial);
  syncAgentConfigToParent();
  StorageHelper.dispatchAgentConfigUpdated();
};

const ChatAgentConfig: React.FC<ChatAgentConfigProps> = ({
  selectedLanguage,
  onLanguageChange,
  chatData
}) => {
  const [langCodes, setLangCodes] = useState<string[]>([]);
  const [defaultLocale, setDefaultLocale] = useState<string>('');
  const apiClient = useApiClient(chatData);
  const token = chatData?.token || null;
  const messageServiceRef = useRef<ChatMessageTemplateService | null>(null);
  const langCodesFetchedRef = useRef(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationEnabled, setNotificationEnabled] = useState<boolean>(() => {
    const savedConfig = StorageHelper.getAgentConfigSync();
    return savedConfig.notification !== false;
  });

  const [openSubmenu, setOpenSubmenu] = useState<'language' | 'notification' | null>(null);

  useEffect(() => {
    if (!token) {
      if (langCodesFetchedRef.current) {
        langCodesFetchedRef.current = false;
      }
      console.warn('Cannot fetch lang-codes: Authentication token is missing');
      return;
    }

    if (langCodesFetchedRef.current) {
      return;
    }

    messageServiceRef.current = new ChatMessageTemplateService(apiClient);

    const fetchLangCodes = async () => {
      if (!messageServiceRef.current || langCodesFetchedRef.current) {
        return;
      }

      if (!token) {
        console.warn('Cannot fetch lang-codes: Authentication token is missing');
        return;
      }

      langCodesFetchedRef.current = true;

      try {
        const response = await messageServiceRef.current.getLangCodes();
        const codes = response.lang_codes || [];
        const defaultLang = response.default || '';
        setLangCodes(codes);
        setDefaultLocale(defaultLang);

        const savedConfig = StorageHelper.getAgentConfigSync();
        const savedLanguage = savedConfig.language;
        const savedNotification = savedConfig.notification !== undefined ? savedConfig.notification : true;
        setNotificationEnabled(savedNotification);

        let languageToSet = '';
        if (savedLanguage && codes.includes(savedLanguage)) {
          languageToSet = savedLanguage;
        } else if (defaultLang && codes.includes(defaultLang)) {
          languageToSet = defaultLang;
        } else if (codes.length > 0) {
          languageToSet = codes[0];
        }

        persistAgentConfig({
          langCodes: codes,
          defaultLocale: defaultLang,
          language: languageToSet || savedLanguage,
          notification: savedNotification
        });

        if (languageToSet && !selectedLanguage) {
          onLanguageChange(languageToSet);
        }
      } catch (error) {
        console.error('Failed to fetch lang-codes:', error);
        langCodesFetchedRef.current = false;
      }
    };

    void fetchLangCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, token]);

  useEffect(() => {
    if (langCodes.length > 0 && (!selectedLanguage || !langCodes.includes(selectedLanguage))) {
      const defaultLang = defaultLocale && langCodes.includes(defaultLocale)
        ? defaultLocale
        : langCodes[0];
      if (defaultLang) {
        onLanguageChange(defaultLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCodes, defaultLocale]);

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsMenuAnchor(null);
    setOpenSubmenu(null);
  };

  const handleLanguageSubmenuOpen = () => {
    if (openSubmenu === 'notification') {
      setOpenSubmenu(null);
    }
    setOpenSubmenu('language');
  };

  const handleLanguageSubmenuClose = () => {
    if (openSubmenu === 'language') {
      setOpenSubmenu(null);
    }
  };

  const handleNotificationSubmenuOpen = () => {
    if (openSubmenu === 'language') {
      setOpenSubmenu(null);
    }
    setOpenSubmenu('notification');
  };

  const handleNotificationSubmenuClose = () => {
    if (openSubmenu === 'notification') {
      setOpenSubmenu(null);
    }
  };

  const handleLanguageSelect = (langCode: string) => {
    onLanguageChange(langCode);
    persistAgentConfig({ language: langCode });
    handleSettingsClose();
  };

  const handleNotificationSelect = (enabled: boolean) => {
    setNotificationEnabled(enabled);
    persistAgentConfig({ notification: enabled });
    handleSettingsClose();
  };

  const getLanguageName = (langCode: string): string => {
    const language = EUROPEAN_LANGUAGES.find(lang => lang.code === langCode);
    return language ? language.name : langCode;
  };

  return (
    <>
      <IconButton
        onClick={handleSettingsClick}
        size="small"
        sx={{ color: 'action.active', padding: '8px' }}
        aria-label="settings"
      >
        <SettingsIcon />
      </IconButton>
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <NestedMenuItem
          label="Language"
          parentMenuOpen={Boolean(settingsMenuAnchor)}
          onOpen={handleLanguageSubmenuOpen}
          onClose={handleLanguageSubmenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {langCodes.map((langCode) => (
            <MenuItem
              key={langCode}
              onClick={() => handleLanguageSelect(langCode)}
              selected={selectedLanguage === langCode}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
                {getLanguageName(langCode)}
                {selectedLanguage === langCode && (
                  <CheckIcon fontSize="small" />
                )}
              </Box>
            </MenuItem>
          ))}
        </NestedMenuItem>
        <NestedMenuItem
          label="Notification"
          parentMenuOpen={Boolean(settingsMenuAnchor)}
          onOpen={handleNotificationSubmenuOpen}
          onClose={handleNotificationSubmenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={() => handleNotificationSelect(true)}
            selected={notificationEnabled === true}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                <NotificationsIcon fontSize="small" />
                On
              </Box>
              {notificationEnabled === true && (
                <CheckIcon fontSize="small" />
              )}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleNotificationSelect(false)}
            selected={notificationEnabled === false}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                <NotificationsOffIcon fontSize="small" />
                Off
              </Box>
              {notificationEnabled === false && (
                <CheckIcon fontSize="small" />
              )}
            </Box>
          </MenuItem>
        </NestedMenuItem>
      </Menu>
    </>
  );
};

export default ChatAgentConfig;
